// Legal / compliance pages — Terms, Privacy, Cancellation & Refund, Contact.
// Served publicly at /terms, /privacy, /refund, /contact and linked in the
// footer. Written in English (the authoritative version + what payment-gateway
// reviewers read); legal accuracy matters more than a Tamil auto-translation.
//
// Positioning is deliberate and TRUE: ChitDesk is B2B SaaS — a software
// subscription. It is NOT a chit fund, deposit-taker or payment processor for
// any operator's scheme. This is the distinction that payment underwriting needs.
//
// Replace every [bracketed] placeholder with the registered entity details
// before publishing. These are working drafts — have them reviewed, not legal
// advice.

export type LegalBlock =
  | { kind: 'h'; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'list'; items: string[] };

export interface LegalDoc {
  slug: string;
  title: string;
  tagline: string;
  updated: string;
  body: LegalBlock[];
}

const h = (text: string): LegalBlock => ({ kind: 'h', text });
const p = (text: string): LegalBlock => ({ kind: 'p', text });
const list = (items: string[]): LegalBlock => ({ kind: 'list', items });

const UPDATED = 'June 2026';
const ENTITY = '[Legal entity name]';
const ADDRESS = '[Registered address]';
const EMAIL = '[support@chitdesk.com]';
const PHONE = '[+91 phone number]';
const CITY = '[city]';

const terms: LegalDoc = {
  slug: 'terms',
  title: 'Terms & Conditions',
  tagline: 'The terms that govern your use of the ChitDesk software subscription.',
  updated: UPDATED,
  body: [
    p(`These Terms & Conditions ("Terms") govern your access to and use of ChitDesk, a software-as-a-service product provided by ${ENTITY} ("ChitDesk", "we", "us"). By creating a workspace or paying for a subscription, you agree to these Terms.`),

    h('1. What ChitDesk is — and is not'),
    p('ChitDesk is business-management software. It provides record-keeping, bookkeeping, reporting and communication tools that help chit-fund and finance operators ("you", the "Operator") run their own business records.'),
    p('ChitDesk is NOT a chit fund, a financial institution, a deposit-taking entity, a money-circulation scheme, or a payment processor for your scheme. We do not solicit, collect, pool, hold, manage or disburse any member contributions, deposits or chit monies. We are not a party to, guarantor of, or intermediary in any chit, savings, lending or similar scheme that you operate. Such schemes are run by you, under your own registrations, into your own bank accounts, entirely outside ChitDesk.'),

    h('2. Your responsibilities'),
    list([
      'You are solely responsible for your own legal and regulatory compliance, including the Chit Funds Act, 1982 and any applicable state rules, registrations, licences and filings.',
      'You are solely responsible for collecting, holding and disbursing all member monies through your own accounts. ChitDesk never receives or holds these funds.',
      'You are responsible for the accuracy of the data you enter and for your dealings with your members.',
      'You must keep your login credentials secure and ensure your staff use the software appropriately.',
    ]),

    h('3. Subscriptions, fees and billing'),
    list([
      'ChitDesk is a paid product with no free tier. Plans (Starter, Growth, Scale, Enterprise) and prices are shown on our pricing page.',
      'Subscription fees are billed in advance, monthly or yearly, and are collected by UPI AutoPay / our payment gateway. Subscriptions auto-renew until cancelled.',
      'Applicable taxes are charged in addition to the listed fees. We may change prices with reasonable prior notice.',
      'If a subscription is unpaid, your workspace becomes read-only — you retain access to view and export your data, but new changes are paused until the subscription is renewed.',
    ]),

    h('4. Acceptable use'),
    list([
      'Do not use ChitDesk for any unlawful purpose, or to operate an unregistered or illegal deposit, money-circulation or "ponzi" scheme.',
      'Do not copy, resell, sublicense, reverse-engineer or attempt to extract the source of the software.',
      'Do not upload unlawful content or use the messaging features to send spam or content that violates applicable law or third-party platform rules.',
    ]),

    h('5. Data and accounts'),
    p('You own the data you enter. Each Operator’s data is stored in an isolated workspace and is not shared with other Operators. We process data in accordance with our Privacy Policy. You may export your data, and you remain the controller of your members’ personal data.'),

    h('6. Suspension and termination'),
    p('We may suspend or limit the service for non-payment, suspected fraud, or breach of these Terms. You may cancel your subscription at any time from Settings → Billing; access continues until the end of your paid period. On termination you may export your data for a reasonable period before deletion.'),

    h('7. Disclaimers and limitation of liability'),
    p('The software is provided "as is" and "as available". To the maximum extent permitted by law, we are not liable for your business outcomes, for disputes between you and your members, or for any losses arising from your scheme. Our total liability for any claim is limited to the subscription fees you paid to us in the twelve (12) months preceding the claim.'),

    h('8. Changes to these Terms'),
    p('We may update these Terms from time to time. Material changes will be notified through the app or by email, and continued use after the change constitutes acceptance.'),

    h('9. Governing law'),
    p(`These Terms are governed by the laws of India, and the courts at ${CITY} shall have exclusive jurisdiction.`),

    h('10. Contact'),
    p(`${ENTITY}, ${ADDRESS}. Email: ${EMAIL}. Phone: ${PHONE}.`),
  ],
};

const privacy: LegalDoc = {
  slug: 'privacy',
  title: 'Privacy Policy',
  tagline: 'How we collect, use and protect information in ChitDesk.',
  updated: UPDATED,
  body: [
    p(`This Privacy Policy explains how ${ENTITY} ("ChitDesk", "we") handles information when you use the ChitDesk software.`),

    h('1. Information we collect'),
    list([
      'Account information: your name, email, phone number and business details.',
      'Content you enter: member records, chits, collections, payments and related business records.',
      'Usage and technical data: log data, device and browser information and IP address, used for security and to operate the service.',
    ]),

    h('2. How we use information'),
    list([
      'To provide, maintain and improve the software.',
      'To process your subscription billing and provide support.',
      'For security, fraud prevention and to meet legal obligations.',
      'To send service communications (e.g. billing, security and important product notices).',
    ]),

    h('3. Tenant isolation'),
    p('Each Operator’s data is stored in its own isolated database and storage. Your business data is not pooled with or visible to other Operators.'),

    h('4. Your members’ data'),
    p('When you add members and record their payments, you are the controller of that personal data and ChitDesk processes it on your behalf to provide the service. You are responsible for having a lawful basis to collect and use your members’ information.'),

    h('5. Sharing and sub-processors'),
    p('We do not sell your data. We share data only with service providers strictly to operate ChitDesk, under appropriate safeguards:'),
    list([
      'Cloud hosting and database infrastructure.',
      'Our payment gateway (for collecting ChitDesk subscription fees only).',
      'Messaging providers used to send reminders you initiate (WhatsApp, SMS, voice and email).',
    ]),

    h('6. Data retention'),
    p('We retain your data while your account is active and for a reasonable period afterwards, then delete or anonymise it, unless a longer period is required by law. You can request export or deletion at any time.'),

    h('7. Security'),
    p('We protect data with encryption in transit, access controls and an audit trail of changes. No system is perfectly secure, but we work to safeguard your information.'),

    h('8. Your rights'),
    p(`You can access, correct, export or request deletion of your account data by contacting us at ${EMAIL}.`),

    h('9. Cookies'),
    p('We use only the cookies/local storage needed to keep you signed in and to remember your language preference.'),

    h('10. Children'),
    p('ChitDesk is a business tool and is not intended for use by anyone under 18.'),

    h('11. Changes and contact'),
    p(`We may update this policy and will notify material changes. Questions: ${ENTITY}, ${ADDRESS}, ${EMAIL}.`),
  ],
};

const refund: LegalDoc = {
  slug: 'refund',
  title: 'Cancellation & Refund Policy',
  tagline: 'How subscription cancellations and refunds work.',
  updated: UPDATED,
  body: [
    p('ChitDesk is a subscription software service. This policy explains how cancellations and refunds work for your ChitDesk subscription.'),

    h('1. Cancellation'),
    p('You may cancel your subscription at any time from Settings → Billing. Cancellation stops future renewals; your access continues until the end of the period you have already paid for. After that, your workspace becomes read-only until you renew.'),

    h('2. Refunds'),
    p('Subscription fees already paid are non-refundable, except in the following cases:'),
    list([
      'A duplicate or clearly erroneous charge.',
      'Where a refund is required by applicable law.',
    ]),
    p(`If you believe you were charged in error, email ${EMAIL} within 7 days of the charge. We will investigate and refund any verified error to your original payment method within 5–7 business days.`),

    h('3. Member payments are not handled by ChitDesk'),
    p('ChitDesk does not collect, hold, refund or mediate any payments between Operators and their members. Any questions about member contributions, chit payouts or related refunds are solely between the Operator and their member, outside ChitDesk.'),

    h('4. Contact'),
    p(`${ENTITY}, ${ADDRESS}. Email: ${EMAIL}. Phone: ${PHONE}.`),
  ],
};

const contact: LegalDoc = {
  slug: 'contact',
  title: 'Contact Us',
  tagline: 'How to reach the team behind ChitDesk.',
  updated: UPDATED,
  body: [
    p('We’re happy to help with anything about your ChitDesk subscription, your account or the software.'),
    h('Business details'),
    list([
      `Business name: ${ENTITY}`,
      `Registered address: ${ADDRESS}`,
      `Email: ${EMAIL}`,
      `Phone: ${PHONE}`,
      'GST / CIN: [if applicable]',
    ]),
    h('Support'),
    p(`Email ${EMAIL} and we’ll respond within 1–2 business days. For account or billing issues, you can also use the in-app help and billing screens once signed in.`),
    p('ChitDesk is software for managing your business records. ChitDesk does not operate a chit fund and does not collect, pool or hold member deposits.'),
  ],
};

export const LEGAL_DOCS: Record<string, LegalDoc> = { terms, privacy, refund, contact };

// Order for footer + cross-links.
export const LEGAL_ORDER: Array<{ slug: string; label: string }> = [
  { slug: 'terms', label: 'Terms' },
  { slug: 'privacy', label: 'Privacy' },
  { slug: 'refund', label: 'Refunds' },
  { slug: 'contact', label: 'Contact' },
];
